import { useMemo, useState, useEffect } from 'react'
import type { RecordRow } from '@/shared/api/generated'

// --- Search index: built once when records change ---

export interface SearchIndex {
  /** recordID → lowercase concatenated text of all searchable fields */
  global: Map<number, string>
  /** tagID → recordID → lowercase concatenated tag values */
  tags: Map<number, Map<number, string>>
  /** column name → recordID → lowercase text */
  columns: Map<string, Map<number, string>>
}

export function buildSearchIndex(records: RecordRow[]): SearchIndex {
  const global = new Map<number, string>()
  const tags = new Map<number, Map<number, string>>()
  const columns = new Map<string, Map<number, string>>()
  const idCol = new Map<number, string>()
  const updatedByCol = new Map<number, string>()
  const updatedAtCol = new Map<number, string>()

  for (const r of records) {
    const parts = [
      String(r.id).padStart(5, '0'),
      r.updated_by || '',
      r.comment || '',
    ]
    for (const [tagIdStr, vals] of Object.entries(r.tag_values || {})) {
      const tagId = Number(tagIdStr)
      const joined = vals.join(' ')
      parts.push(joined)

      let tagMap = tags.get(tagId)
      if (!tagMap) {
        tagMap = new Map()
        tags.set(tagId, tagMap)
      }
      tagMap.set(r.id, joined.toLowerCase())
    }
    global.set(r.id, parts.join('\0').toLowerCase())

    idCol.set(r.id, String(r.id).padStart(5, '0'))
    updatedByCol.set(r.id, (r.updated_by || '').toLowerCase())
    updatedAtCol.set(r.id, new Date(r.updated_at).toLocaleDateString().toLowerCase())
  }
  columns.set('id', idCol)
  columns.set('updated_by', updatedByCol)
  columns.set('updated_at', updatedAtCol)
  return { global, tags, columns }
}

// --- Filtering: uses pre-built index ---

export function filterRecords(
  records: RecordRow[],
  index: SearchIndex,
  tagFilters: Record<number, string>,
  columnFilters: Record<string, string>,
  globalFilter: string,
): RecordRow[] {
  let filtered = records

  for (const [tagIdStr, text] of Object.entries(tagFilters)) {
    if (!text) continue
    const tagId = Number(tagIdStr)
    const lower = text.toLowerCase()
    const tagMap = index.tags.get(tagId)
    if (!tagMap) { filtered = []; break }
    filtered = filtered.filter(r => tagMap.get(r.id)?.includes(lower) ?? false)
  }

  for (const [col, text] of Object.entries(columnFilters)) {
    if (!text) continue
    const lower = text.toLowerCase()
    const colMap = index.columns.get(col)
    if (!colMap) continue
    filtered = filtered.filter(r => colMap.get(r.id)?.includes(lower) ?? false)
  }

  if (globalFilter) {
    const lower = globalFilter.toLowerCase()
    filtered = filtered.filter(r => index.global.get(r.id)?.includes(lower) ?? false)
  }

  return filtered
}

// --- Sorting: separate from filtering ---

export function sortRecords(
  records: RecordRow[],
  sortColumn: string,
  sortDirection: 'asc' | 'desc',
): RecordRow[] {
  const sorted = [...records].sort((a, b) => {
    let cmp = 0
    if (sortColumn === 'id') {
      cmp = a.id - b.id
    } else if (sortColumn === 'updated_at') {
      cmp = a.updated_at.localeCompare(b.updated_at)
    } else if (sortColumn === 'updated_by') {
      cmp = (a.updated_by || '').localeCompare(b.updated_by || '')
    } else {
      const tagId = Number(sortColumn)
      const va = (a.tag_values?.[tagId] || []).join(', ')
      const vb = (b.tag_values?.[tagId] || []).join(', ')
      cmp = va.localeCompare(vb)
    }
    return sortDirection === 'asc' ? cmp : -cmp
  })
  return sorted
}

// --- Hooks ---

export function useRecordFilters(
  records: RecordRow[],
  tagFilters: Record<number, string>,
  columnFilters: Record<string, string>,
  globalFilter: string,
  sortColumn: string,
  sortDirection: 'asc' | 'desc',
): RecordRow[] {
  const index = useMemo(() => buildSearchIndex(records), [records])
  const filtered = useMemo(
    () => filterRecords(records, index, tagFilters, columnFilters, globalFilter),
    [records, index, tagFilters, columnFilters, globalFilter],
  )
  return useMemo(
    () => sortRecords(filtered, sortColumn, sortDirection),
    [filtered, sortColumn, sortDirection],
  )
}

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

export function getAutocompleteValues(records: RecordRow[], tagId: number): string[] {
  const set = new Set<string>()
  for (const r of records) {
    for (const v of r.tag_values?.[tagId] || []) {
      set.add(v)
    }
  }
  return Array.from(set).sort()
}
